import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  ChevronLeft, 
  ChevronRight, 
  Link2, 
  Upload, 
  Sparkles, 
  CheckCircle2, 
  ImagePlus, 
  Loader2, 
  FolderUp, 
  SlidersHorizontal,
  CloudUpload
} from 'lucide-react';
import { BannerSlide } from '../types';

interface HeroBannerSliderProps {
  slides: BannerSlide[];
  onOpenUploadModal: () => void;
  onQuickAddToCart?: (productCode: string) => void;
  onSaveSlides?: (updated: BannerSlide[]) => void;
}

export const HeroBannerSlider: React.FC<HeroBannerSliderProps> = ({
  slides,
  onOpenUploadModal,
  onQuickAddToCart,
  onSaveSlides,
}) => {
  const activeSlides = slides.filter((s) => s.isActive);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Direct Slider Bucket Upload State
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgressMsg, setUploadProgressMsg] = useState('');
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  // Quick URL Input Bar state
  const [isQuickUrlOpen, setIsQuickUrlOpen] = useState(false);
  const [inputUrl, setInputUrl] = useState('');
  const [saveToast, setSaveToast] = useState<{ open: boolean; url: string; msg: string }>({
    open: false,
    url: '',
    msg: ''
  });

  // Check if accessed through AI Studio developer environment or with dev parameters
  const isDeveloperMode = React.useMemo(() => {
    if (typeof window === 'undefined') return false;
    const href = window.location.href.toLowerCase();
    const hostname = window.location.hostname.toLowerCase();
    
    const isAiStudioDomain = hostname.includes('ai.studio') || href.includes('pinkgiffarine-online-store.ai.studio');
    const isDevHost = hostname.includes('run.app') || hostname.includes('localhost') || hostname.includes('127.0.0.1');
    const hasDevQuery = new URLSearchParams(window.location.search).has('dev') || 
                        new URLSearchParams(window.location.search).has('admin') ||
                        new URLSearchParams(window.location.search).has('developer');

    return isAiStudioDomain || isDevHost || hasDevQuery;
  }, []);

  // Touch swipe support
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const minSwipeDistance = 50;

  // Change slide
  const goToSlide = useCallback((newIndex: number) => {
    setCurrentIndex(newIndex);
  }, []);

  // Auto-play interval
  useEffect(() => {
    if (isPaused || activeSlides.length <= 1 || isUploading) return;

    timerRef.current = setInterval(() => {
      goToSlide((currentIndex + 1) % activeSlides.length);
    }, 5500);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isPaused, activeSlides.length, currentIndex, goToSlide, isUploading]);

  // Clamp index if slides change
  useEffect(() => {
    if (currentIndex >= activeSlides.length && activeSlides.length > 0) {
      setCurrentIndex(0);
    }
  }, [activeSlides.length, currentIndex]);

  const handlePrev = useCallback(() => {
    const nextIdx = currentIndex === 0 ? activeSlides.length - 1 : currentIndex - 1;
    goToSlide(nextIdx);
  }, [activeSlides.length, currentIndex, goToSlide]);

  const handleNext = useCallback(() => {
    const nextIdx = (currentIndex + 1) % activeSlides.length;
    goToSlide(nextIdx);
  }, [activeSlides.length, currentIndex, goToSlide]);

  const onTouchStartHandle = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMoveHandle = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEndHandle = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe) {
      handleNext();
    } else if (isRightSwipe) {
      handlePrev();
    }
  };

  // Direct Upload to Storage Bucket API Handler
  const uploadImageFileToBucket = async (file: File) => {
    if (!file) return;

    if (file.size > 20 * 1024 * 1024) {
      alert('ขนาดไฟล์ภาพต้องไม่เกิน 20MB');
      return;
    }

    const currentSlide = activeSlides[currentIndex] || slides[0];
    const targetSlideId = currentSlide?.id || `banner-${Date.now()}`;

    setIsUploading(true);
    setUploadProgressMsg('กำลังส่งภาพเข้า Storage Bucket และประมวลผล WebP...');

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64Data = e.target?.result as string;
        try {
          const res = await fetch('/api/sliders/upload', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              imageBase64: base64Data,
              slideId: targetSlideId,
              slideIndex: currentIndex,
              title: currentSlide?.title
            })
          });

          const data = await res.json();
          if (res.ok && data.success && data.url) {
            const permanentStorageUrl = data.url;
            const updatedSlides = slides.map((s, idx) =>
              (s.id === targetSlideId || idx === currentIndex) ? { ...s, imageUrl: permanentStorageUrl } : s
            );

            if (onSaveSlides) {
              onSaveSlides(updatedSlides);
            }

            setSaveToast({
              open: true,
              url: permanentStorageUrl,
              msg: `อัปโหลดเข้า Storage Bucket สำเร็จ! พร้อมสร้างลิงก์ถาวร: ${permanentStorageUrl}`
            });
            setTimeout(() => setSaveToast({ open: false, url: '', msg: '' }), 5000);
          } else {
            throw new Error(data.error || 'Failed to upload to slider storage bucket');
          }
        } catch (serverErr: any) {
          console.error('Upload to slider bucket failed:', serverErr);
          // Fallback to local Base64 URL if offline or network error
          const fallbackSlides = slides.map((s, idx) =>
            (s.id === targetSlideId || idx === currentIndex) ? { ...s, imageUrl: base64Data } : s
          );
          if (onSaveSlides) onSaveSlides(fallbackSlides);
          setSaveToast({
            open: true,
            url: 'Local Cache',
            msg: 'บันทึกภาพลงหน่วยความจำชั่วคราวเรียบร้อยแล้ว'
          });
          setTimeout(() => setSaveToast({ open: false, url: '', msg: '' }), 4000);
        } finally {
          setIsUploading(false);
          setUploadProgressMsg('');
        }
      };

      reader.readAsDataURL(file);
    } catch (err: any) {
      console.error('File read error:', err);
      setIsUploading(false);
      setUploadProgressMsg('');
      alert('เกิดข้อผิดพลาดในการอ่านไฟล์ภาพ');
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadImageFileToBucket(file);
    }
    // Reset file input value so user can upload the same filename again if needed
    e.target.value = '';
  };

  // Instant apply URL and auto-download to bucket
  const handleInstantApplyUrl = async (urlToApply: string) => {
    const trimmed = urlToApply.trim();
    if (!trimmed) return;

    const currentSlide = activeSlides[currentIndex] || slides[0];
    const targetSlideId = currentSlide?.id || `banner-${Date.now()}`;

    setIsUploading(true);
    setUploadProgressMsg('กำลังดึงภาพจาก URL และบันทึกลง Storage Bucket...');

    try {
      const res = await fetch('/api/sliders/upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          imageUrl: trimmed,
          slideId: targetSlideId,
          slideIndex: currentIndex,
          title: currentSlide?.title
        })
      });

      const data = await res.json();
      const permanentStorageUrl = (res.ok && data.success && data.url) ? data.url : trimmed;

      const updatedSlides = slides.map((s, idx) =>
        (s.id === targetSlideId || idx === currentIndex) ? { ...s, imageUrl: permanentStorageUrl } : s
      );

      if (onSaveSlides) {
        onSaveSlides(updatedSlides);
      }

      setSaveToast({
        open: true,
        url: permanentStorageUrl,
        msg: `ดึงภาพจาก URL เข้า Storage Bucket สำเร็จ! สร้างลิงก์ถาวร: ${permanentStorageUrl}`
      });
      setInputUrl('');
      setIsQuickUrlOpen(false);
      setTimeout(() => setSaveToast({ open: false, url: '', msg: '' }), 5000);
    } catch (err) {
      console.warn('Fallback direct url apply:', err);
      const updatedSlides = slides.map((s, idx) =>
        (s.id === targetSlideId || idx === currentIndex) ? { ...s, imageUrl: trimmed } : s
      );
      if (onSaveSlides) onSaveSlides(updatedSlides);
      setSaveToast({
        open: true,
        url: trimmed,
        msg: 'บันทึกลิงก์รูปภาพเรียบร้อยแล้ว'
      });
      setInputUrl('');
      setIsQuickUrlOpen(false);
      setTimeout(() => setSaveToast({ open: false, url: '', msg: '' }), 4000);
    } finally {
      setIsUploading(false);
      setUploadProgressMsg('');
    }
  };

  // Drag and Drop files directly onto slider
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingOver(true);
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingOver(false);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      uploadImageFileToBucket(file);
    }
  };

  if (activeSlides.length === 0) return null;

  const current = activeSlides[currentIndex] || activeSlides[0];

  return (
    <div className="w-full px-0 pt-0 pb-1 sm:pb-2">
      {/* Hidden Native File Input for Instant 1-Click Upload */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileInputChange}
        accept="image/png,image/jpeg,image/webp,image/jpg,image/gif"
        className="hidden"
      />

      {/* Toast Notification when Image/URL is saved to Storage Bucket */}
      {saveToast.open && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 mb-2">
          <div className="px-4 py-3 bg-gradient-to-r from-emerald-600 to-teal-700 text-white text-xs sm:text-sm font-bold rounded-2xl shadow-xl flex items-center justify-between border border-emerald-400/40 animate-fadeIn">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
                <CheckCircle2 className="w-4 h-4 text-emerald-100" />
              </div>
              <div>
                <p className="font-bold">{saveToast.msg}</p>
                {saveToast.url && saveToast.url.startsWith('/storage') && (
                  <p className="text-[11px] text-emerald-100/90 font-mono mt-0.5">
                    Storage URL: <span className="underline">{saveToast.url}</span> (ดึงอัตโนมัติทุกครั้งที่เข้าชม)
                  </p>
                )}
              </div>
            </div>
            <span className="text-xs bg-white/20 px-2.5 py-1 rounded-full text-emerald-50 font-mono tracking-wider shrink-0 ml-3">
              BUCKET SYNCED
            </span>
          </div>
        </div>
      )}

      {/* Main Full-Width Clean Banner Container (ขยายเต็มขอบซ้าย-ขวา 100% ลดระยะห่างบน-ล่าง) */}
      <div
        id="hero-banner-slider-container"
        className={`relative w-full rounded-none overflow-hidden bg-white border-b border-slate-200/60 shadow-xs group select-none transition-all ${
          isDraggingOver ? 'ring-4 ring-blue-500 ring-inset opacity-90' : ''
        }`}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onTouchStart={onTouchStartHandle}
        onTouchMove={onTouchMoveHandle}
        onTouchEnd={onTouchEndHandle}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Drag Over Visual Overlay */}
        {isDraggingOver && (
          <div className="absolute inset-0 z-40 bg-blue-600/75 backdrop-blur-xs flex flex-col items-center justify-center text-white p-6 animate-fadeIn">
            <CloudUpload className="w-16 h-16 mb-3 animate-bounce text-blue-100" />
            <p className="text-xl font-black">ปล่อยไฟล์รูปภาพที่นี่เพื่ออัปโหลด</p>
            <p className="text-xs text-blue-100 mt-1">ระบบจะบันทึกเข้า Storage Bucket และสร้างลิงก์แสดงผลทันที</p>
          </div>
        )}

        {/* Uploading In-Progress Loading State Overlay */}
        {isUploading && (
          <div className="absolute inset-0 z-40 bg-slate-900/80 backdrop-blur-sm flex flex-col items-center justify-center text-white p-6 animate-fadeIn">
            <div className="w-14 h-14 rounded-2xl bg-blue-600/30 border border-blue-400/50 flex items-center justify-center mb-3">
              <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
            </div>
            <p className="text-base sm:text-lg font-bold text-white tracking-wide">
              {uploadProgressMsg || 'กำลังจัดเก็บภาพสไลเดอร์ลง Storage Bucket...'}
            </p>
            <p className="text-xs text-slate-300 mt-1.5 font-mono">
              Generating Immutable Storage URL & Optimizing WebP
            </p>
          </div>
        )}

        {/* Banner Image Stage (Tight Proportions: aspect-[2/1] on mobile to eliminate excessive white bars) */}
        <div className="relative w-full aspect-[2/1] sm:aspect-[2.2/1] md:aspect-[24/9.5] lg:aspect-[24/9.1] flex items-center justify-center overflow-hidden bg-white">
          {current?.imageUrl ? (
            <img
              key={`banner-img-${current.id}-${currentIndex}`}
              src={current.imageUrl}
              alt={current.title || 'แบนเนอร์กิฟฟารีน'}
              className="w-full h-full object-contain object-center transition-all duration-700 animate-fadeIn"
              loading="eager"
              decoding="async"
              onClick={() => {
                if (current.productCode && onQuickAddToCart) {
                  onQuickAddToCart(current.productCode);
                }
              }}
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center p-6 text-slate-400 bg-slate-100">
              <Upload className="w-12 h-12 mb-2 text-slate-300" />
              <p className="text-sm font-bold">ยังไม่มีรูปภาพแบนเนอร์ในสไลด์นี้</p>
              <p className="text-xs text-slate-500">คลิกปุ่ม "อัปโหลดรูปภาพใหม่" ด้านบนขวาเพื่อใส่รูป</p>
            </div>
          )}

          {/* Left Arrow Button */}
          {activeSlides.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handlePrev();
              }}
              className="absolute left-3 sm:left-6 md:left-8 top-1/2 -translate-y-1/2 w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-white/85 hover:bg-white text-slate-700 hover:text-blue-600 shadow-lg backdrop-blur-md flex items-center justify-center transition-all opacity-80 group-hover:opacity-100 hover:scale-105 active:scale-95 cursor-pointer z-20 border border-slate-200/60"
              title="สไลด์ก่อนหน้า"
              aria-label="Previous Slide"
            >
              <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
          )}

          {/* Right Arrow Button */}
          {activeSlides.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleNext();
              }}
              className="absolute right-3 sm:right-6 md:right-8 top-1/2 -translate-y-1/2 w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-white/85 hover:bg-white text-slate-700 hover:text-blue-600 shadow-lg backdrop-blur-md flex items-center justify-center transition-all opacity-80 group-hover:opacity-100 hover:scale-105 active:scale-95 cursor-pointer z-20 border border-slate-200/60"
              title="สไลด์ถัดไป"
              aria-label="Next Slide"
            >
              <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
          )}

          {/* Slide Indicator Dots with Slide Count */}
          {activeSlides.length > 1 && (
            <div className="absolute bottom-3 sm:bottom-5 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1.5 sm:gap-2 bg-black/45 backdrop-blur-md px-3.5 py-1.5 rounded-full border border-white/20 shadow-md">
              {activeSlides.map((s, idx) => (
                <button
                  key={s.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    goToSlide(idx);
                  }}
                  aria-label={`Go to slide ${idx + 1}`}
                  className="p-1 flex items-center justify-center rounded-full cursor-pointer transition-all"
                >
                  <span
                    className={`transition-all duration-300 rounded-full block ${
                      currentIndex === idx
                        ? 'w-6 sm:w-7 h-2 bg-white shadow-xs'
                        : 'w-2 h-2 bg-white/50 hover:bg-white/80'
                    }`}
                  />
                </button>
              ))}
            </div>
          )}

          {/* ======================================================== */}
          {/* BRAND NEW MODERN UPLOAD BUTTON & SLIDER CONTROL ARCHITECTURE */}
          {/* ======================================================== */}
          {isDeveloperMode && (
            <div className="absolute top-3 right-3 sm:top-4 sm:right-6 md:right-8 z-30 flex flex-wrap items-center gap-2">
              {/* 1. BRAND NEW REDESIGNED HERO UPLOAD BUTTON */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  fileInputRef.current?.click();
                }}
                disabled={isUploading}
                className="group/btn relative px-3.5 sm:px-4 py-2 bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 hover:from-blue-500 hover:to-indigo-600 text-white text-xs sm:text-sm font-bold rounded-2xl shadow-lg hover:shadow-xl hover:shadow-blue-500/30 backdrop-blur-md transition-all duration-300 flex items-center gap-2 active:scale-95 cursor-pointer border border-blue-300/40"
                title="คลิกเพื่อเลือกไฟล์รูปภาพจากอุปกรณ์ และอัปโหลดเข้าสู่ Storage Bucket ทันที"
              >
                <div className="w-5 h-5 rounded-lg bg-white/20 flex items-center justify-center group-hover/btn:rotate-12 transition-transform">
                  <ImagePlus className="w-3.5 h-3.5 text-white" />
                </div>
                <div className="flex flex-col text-left">
                  <span className="leading-tight">อัปโหลดรูปสไลเดอร์ {currentIndex + 1}</span>
                  <span className="text-[9px] text-blue-100/80 font-normal leading-none hidden sm:inline">
                    จัดเก็บเข้า Bucket & ลิงก์ถาวร
                  </span>
                </div>
                <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse shadow-xs" />
              </button>

              {/* 2. QUICK DIRECT URL BUTTON */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsQuickUrlOpen(!isQuickUrlOpen);
                }}
                className="px-3 py-2 bg-white/90 hover:bg-white text-slate-700 hover:text-blue-600 text-xs font-bold rounded-2xl shadow-md backdrop-blur-md transition-all flex items-center gap-1.5 active:scale-95 cursor-pointer border border-slate-200/80"
                title="ใส่ URL รูปภาพโดยตรงเพื่อดึงเข้าสู่ Storage Bucket"
              >
                <Link2 className="w-3.5 h-3.5 text-blue-600" />
                <span className="hidden sm:inline">วาง URL</span>
              </button>

              {/* 3. FULL SLIDE MANAGER MODAL TRIGGER */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenUploadModal();
                }}
                className="px-3 py-2 bg-white/90 hover:bg-white text-slate-700 hover:text-blue-600 text-xs font-bold rounded-2xl border border-slate-200/80 shadow-md backdrop-blur-md transition-all flex items-center gap-1.5 active:scale-95 cursor-pointer"
                title="จัดการ ลำดับ/ข้อความ/ลิงก์ สินค้าของทุกสไลด์"
              >
                <SlidersHorizontal className="w-3.5 h-3.5 text-slate-600" />
                <span className="hidden sm:inline">จัดการสไลด์ทั้งหมด</span>
              </button>
            </div>
          )}
        </div>

        {/* Quick URL Input Drawer (พร้อมตรรกะบันทึกเข้า Storage Bucket ทันที) */}
        {isQuickUrlOpen && (
          <div className="w-full bg-white border-t border-slate-200 shadow-inner py-3 sm:py-3.5 px-4 sm:px-6 md:px-8 animate-fadeIn">
            <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center gap-2.5">
              <div className="flex items-center gap-2 text-xs font-bold text-slate-700 shrink-0">
                <FolderUp className="w-4 h-4 text-blue-600" />
                <span>URL รูปภาพสไลด์ที่ {currentIndex + 1}:</span>
              </div>
              <div className="flex-1 w-full flex items-center gap-2">
                <input
                  type="url"
                  placeholder="วางลิงก์รูปภาพ (เช่น https://example.com/banner.jpg) ระบบจะจัดเก็บเข้า Storage ทันที"
                  value={inputUrl}
                  onChange={(e) => setInputUrl(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleInstantApplyUrl(inputUrl);
                    }
                  }}
                  className="flex-1 px-3.5 py-2 text-xs sm:text-sm bg-slate-50 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white font-mono"
                />
                <button
                  type="button"
                  disabled={isUploading}
                  onClick={() => handleInstantApplyUrl(inputUrl)}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm font-bold rounded-xl shadow-xs transition-all active:scale-95 shrink-0 cursor-pointer flex items-center gap-1.5"
                >
                  {isUploading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4 text-blue-200" />
                  )}
                  <span>ดึงเข้า Bucket & บันทึก</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
